import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FilePdf, Download, Sparkle, ShieldCheck, Printer, ArrowsClockwise, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError } from "@/lib/api";
import PdfDownloadOverlay from "@/components/PdfDownloadOverlay";

const STATUSES = [
  "submitted", "under_review", "clarification_required",
  "discovery_call_required", "proposal_in_progress", "won", "lost", "closed",
];

export default function IdeaLabAdminDetail() {
  const { ref } = useParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfStage, setPdfStage] = useState("preparing");
  const [regenBusy, setRegenBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  const resendPack = async () => {
    const email = sub?.contact?.email;
    if (!email) {
      toast.error("This submission has no contact email on file.");
      return;
    }
    if (!window.confirm(`Re-email the refreshed discovery pack (PDF + confirmation email) to ${email}?\n\nThis sends the latest AI summary. Use this after Regenerate AI to give the customer the corrected version.`)) return;
    setResendBusy(true);
    try {
      const { data } = await api.post(`/idealab/admin/submissions/${ref}/resend-pack`);
      toast.success(`Discovery pack re-sent to ${data?.to_email || email}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not re-send.");
    } finally {
      setResendBusy(false);
    }
  };

  const regenerateAi = async () => {
    if (!window.confirm("Regenerate the AI discovery summary and the internal assessment for this submission?\n\nThis replaces the existing summary with a new one using the latest prompt.")) return;
    setRegenBusy(true);
    try {
      const { data } = await api.post(`/idealab/admin/submissions/${ref}/regenerate-ai`);
      if (data?.summary_regenerated && data?.assessment_regenerated) {
        toast.success("AI summary and internal assessment regenerated");
      } else if (data?.summary_regenerated) {
        toast.warning("Summary regenerated; internal assessment failed — try again");
      } else if (data?.assessment_regenerated) {
        toast.warning("Assessment regenerated; customer summary failed — try again");
      } else {
        toast.error("Both regenerations failed — try again in a moment");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not regenerate.");
    } finally {
      setRegenBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/idealab/admin/submissions/${ref}`);
      setSub(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ref]);

  const downloadPdf = async (e) => {
    e?.preventDefault?.();
    setPdfBusy(true);
    setPdfStage("preparing");
    try {
      const res = await api.get(`/idealab/admin/submissions/${ref}/pdf`, { responseType: "blob" });
      setPdfStage("downloading");
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Appistry-IdeaLab-${ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setPdfStage("done");
      setTimeout(() => setPdfBusy(false), 900);
    } catch (err) {
      toast.error("Could not generate PDF — please try again.");
      setPdfBusy(false);
    }
  };

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      await api.put(`/idealab/admin/submissions/${ref}/status`, { status });
      toast.success("Status updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update status");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-zinc-500 font-mono">Loading…</div>;
  if (!sub) return <div className="p-10">Not found.</div>;

  const c = sub.contact || {};
  const a = sub.answers || {};
  const ai = sub.ai_summary || {};
  const la = sub.lead_assessment || {};
  const clicks = sub.demo_clicks || { total: 0, by_slug: {}, events: [] };

  return (
    <div className="p-8 md:p-10 max-w-5xl" data-testid="admin-idealab-detail">
      <PdfDownloadOverlay open={pdfBusy} stage={pdfStage} reference={sub.reference_number} />
      <Link to="/admin/idealab" className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-600 hover:text-zinc-900 mb-6" data-testid="back-to-idealab">
        <ArrowLeft size={14} weight="bold" /> Back to submissions
      </Link>

      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <p className="overline mb-2">Reference</p>
          <h1 className="text-3xl font-bold tracking-tight font-mono">{sub.reference_number}</h1>
          <p className="text-sm text-zinc-500 mt-1">Submitted {formatDate(sub.submitted_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={regenerateAi}
            disabled={regenBusy}
            className="btn-ghost-ink disabled:opacity-50"
            data-testid="admin-idealab-regenerate-ai"
            title="Re-run the AI summary and internal assessment with the latest prompt"
          >
            <ArrowsClockwise size={14} weight="bold" className={regenBusy ? "animate-spin" : ""} />
            {regenBusy ? "Regenerating…" : "Regenerate AI"}
          </button>
          <button
            onClick={resendPack}
            disabled={resendBusy || !sub.contact?.email}
            className="btn-ghost-ink disabled:opacity-50"
            data-testid="admin-idealab-resend-pack"
            title="Re-email the customer their discovery pack (PDF + confirmation email) with the latest AI summary"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {resendBusy ? "Sending…" : "Resend to customer"}
          </button>
          <Link
            to={`/admin/idealab/${sub.reference_number}/brief`}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost-ink"
            data-testid="admin-idealab-brief"
            title="Open a print-optimised one-page discovery brief"
          >
            <Printer size={14} weight="bold" /> Print brief
          </Link>
          <a
            href={`${API_BASE}/idealab/admin/submissions/${sub.reference_number}/pdf`}
            onClick={downloadPdf}
            className="btn-signal"
            data-testid="admin-idealab-pdf"
            aria-busy={pdfBusy}
          >
            <FilePdf size={14} weight="fill" /> Download PDF
          </a>
        </div>
      </div>

      {/* Status */}
      <Section title="Status">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              disabled={saving}
              onClick={() => updateStatus(s)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${sub.status === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:border-zinc-400"}`}
              data-testid={`status-set-${s}`}
            >
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </Section>

      {/* Lead assessment */}
      <Section title="Lead assessment" icon={<Sparkle size={16} weight="fill" className="text-[hsl(var(--appistry-signal))]" />}>
        {Object.keys(la).length === 0 ? (
          <p className="text-zinc-500 text-sm">Generating…</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Readiness" value={la.lead_readiness} />
            <Stat label="Complexity" value={la.complexity} />
            <Stat label="Confidence" value={la.confidence} />
            <Stat label="Completeness" value={la.completeness_score != null ? `${la.completeness_score}/100` : "—"} />
            {Array.isArray(la.missing_information) && la.missing_information.length > 0 && (
              <div className="col-span-2 md:col-span-4">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Missing info</div>
                <ul className="list-disc list-inside text-sm text-zinc-700 space-y-1">
                  {la.missing_information.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Demo engagement — click attribution from the confirmation email demo links */}
      <Section title="Demo engagement" icon={<Sparkle size={16} weight="fill" className="text-[hsl(var(--appistry-signal))]" />}>
        {clicks.total === 0 ? (
          <p className="text-zinc-500 text-sm" data-testid="demo-clicks-empty">No demo clicks yet from this customer's confirmation email.</p>
        ) : (
          <div data-testid="demo-clicks">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <Stat label="Total clicks" value={String(clicks.total)} />
              <Stat label="Unique demos" value={String(Object.keys(clicks.by_slug).length)} />
              <Stat label="First click" value={formatDate((clicks.events[0] || {}).clicked_at)} />
              <Stat label="Last click" value={formatDate((clicks.events[clicks.events.length - 1] || {}).clicked_at)} />
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Clicks by demo</div>
            <ul className="divide-y divide-zinc-200 border border-zinc-200">
              {Object.entries(clicks.by_slug).sort((x, y) => y[1] - x[1]).map(([slug, n]) => (
                <li key={slug} className="px-4 py-2.5 flex items-center justify-between text-sm" data-testid={`demo-clicks-row-${slug}`}>
                  <Link to={`/demos/${slug}`} target="_blank" rel="noreferrer" className="font-semibold hover:underline">{slug}</Link>
                  <span className="font-mono text-xs text-zinc-600">{n} {n === 1 ? "click" : "clicks"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {/* AI summary */}
      <Section title="AI discovery summary (what the customer sees)" icon={<Sparkle size={16} weight="fill" className="text-[hsl(var(--appistry-signal))]" />}>
        {Object.keys(ai).length === 0 ? (
          <p className="text-zinc-500 text-sm">Generating…</p>
        ) : (
          <div className="space-y-4 text-sm">
            <Block label="Your situation (their view)" body={ai.business_challenge} />
            <Block label="How we'd shape this" body={ai.proposed_solution} />
            <Block label="Who this is for" body={ai.target_users} />
            {Array.isArray(ai.key_outcomes) && (
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">What success looks like</div>
                <ul className="list-disc list-inside text-zinc-800">{ai.key_outcomes.map((o, i) => <li key={i}>{o}</li>)}</ul>
              </div>
            )}
            <Block label="Likely shape of the build" body={ai.tech_approach} />
            <Block label="What we love about their idea" body={ai.what_we_love} />
            {Array.isArray(ai.things_to_explore) && ai.things_to_explore.length > 0 && (
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Questions worth chatting through</div>
                <ul className="list-disc list-inside text-zinc-800">{ai.things_to_explore.map((q, i) => <li key={i}>{q}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Internal Appistry-only assessment */}
      <Section title="Internal assessment (Appistry only — never shown to customer)" icon={<Sparkle size={16} weight="fill" className="text-amber-500" />}>
        {Object.keys(la).length === 0 ? (
          <p className="text-zinc-500 text-sm">Generating…</p>
        ) : (
          <div className="space-y-4 text-sm">
            <Block label="Complexity rationale" body={la.complexity_rationale} />
            <Block label="Budget vs scope alignment" body={la.budget_scope_alignment} />
            <Block label="Recommended next step" body={la.recommended_next_step ? `${la.recommended_next_step}. ${la.next_step_rationale || ""}` : la.next_step} />
            <Block label="Internal notes" body={la.internal_notes} />
          </div>
        )}
      </Section>

      {/* Contact */}
      <Section title="Contact">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Row label="Contact name" value={c.full_name} />
          <Row label="Business" value={c.business_name || c.organisation} />
          <Row label="Email" value={c.email} />
          <Row label="Website" value={c.website} />
          <Row label="Telephone" value={c.telephone} />
          <Row label="Job title" value={c.job_title} />
          <Row label="Preferred contact" value={c.preferred_contact} />
          <Row label="Referral" value={c.referral_source} />
        </dl>
      </Section>

      {/* Answers */}
      <Section title="Discovery answers">
        <div className="space-y-5 text-sm">
          <h3 className="font-semibold text-zinc-900">Section 1 · About you & your business</h3>
          <Block label="Business description" body={a.business_description} />
          <Block label="The idea" body={a.idea_description} />
          <Block label="Inspiration" body={a.inspiration} />
          <Block label="Success vision (12 months)" body={a.success_vision} />
          <Block label="Top three for V1" body={a.top_three_v1} />

          <h3 className="font-semibold text-zinc-900 pt-3">Section 2 · Customers</h3>
          <Block label="Who is this for" body={a.customer_who} />
          <Block label="Challenges they face" body={a.customer_challenges} />
          <Block label="Outcomes they want" body={a.customer_outcomes} />
          <Block label="Where customers are based" body={a.customer_location} />
          <Block label="Existing audience" body={(a.customer_audience || []).join(", ")} />

          <h3 className="font-semibold text-zinc-900 pt-3">Section 3 · Offering</h3>
          <Block label="Offerings" body={(a.offerings || []).join(", ")} />
          <Block label="Already have content?" body={a.has_existing_content} />
          <Block label="Will continue creating?" body={a.will_continue_creating} />
          <Block label="Amount of existing content" body={a.content_amount} />

          <h3 className="font-semibold text-zinc-900 pt-3">Section 4 · Access</h3>
          <Block label="Access model" body={a.access_model} />
          <Block label="Membership levels?" body={a.membership_levels} />
          <Block label="User accounts?" body={a.user_accounts} />
          <Block label="Sign-in methods" body={(a.signin_methods || []).join(", ")} />

          <h3 className="font-semibold text-zinc-900 pt-3">Section 5 · AI & personalisation</h3>
          <Block label="AI features" body={(a.ai_features || []).join(", ")} />
          <Block label="Content to support AI?" body={a.ai_content_support} />

          <h3 className="font-semibold text-zinc-900 pt-3">Section 6 · Existing setup</h3>
          <Block label="Existing setup" body={(a.existing_setup || []).join(", ")} />
          <Block label="Domain name" body={a.domain_name} />
          <Block label="Domain manager" body={a.domain_manager} />
          <Block label="Domain access" body={a.domain_access} />
          <Block label="Pro email wanted" body={a.pro_email_wanted} />
          <Block label="Accepts payments online" body={a.accepts_payments_online} />
          <Block label="Payment platform" body={a.payment_platform} />

          <h3 className="font-semibold text-zinc-900 pt-3">Section 7 · Brand & style</h3>
          <Block label="Brand in three words" body={a.brand_three_words} />
          <Block label="Brand feelings" body={(a.brand_feelings || []).join(", ")} />
          <Block label="Existing brand assets" body={(a.brand_assets || []).join(", ")} />
          <Block label="Colours liked" body={a.brand_colors_like} />
          <Block label="Colours disliked" body={a.brand_colors_dislike} />
          <Block label="Sites/apps admired" body={a.brand_inspirations} />

          <h3 className="font-semibold text-zinc-900 pt-3">Section 8 · Budget & next steps</h3>
          <Block label="Budget statement" body={a.budget_statement} />
          <Block label="Investment band" body={a.budget_band} />
          <Block label="Target launch date" body={a.target_launch_date} />
          <Block label="Anything else" body={a.anything_else} />
        </div>
      </Section>

      {/* Files */}
      {(sub.file_ids || []).length > 0 && (
        <Section title="Uploaded files">
          <ul className="divide-y divide-zinc-200 border border-zinc-200" data-testid="admin-idealab-files">
            {sub.file_ids.map((id, i) => (
              <li key={id} className="px-4 py-3 flex items-center justify-between">
                <span className="text-sm">{sub.file_names?.[i] || `file-${i + 1}`}</span>
                <a
                  href={`${API_BASE}/idealab/admin/submissions/${sub.reference_number}/files/${id}`}
                  target="_blank" rel="noreferrer"
                  className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                  data-testid={`admin-idealab-file-${id}`}
                >
                  <Download size={14} /> Download
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* AI turns transcript */}
      {(sub.ai_turns || []).length > 0 && (
        <Section title="Discovery conversation">
          <div className="space-y-4">
            {sub.ai_turns.map((t, i) => (
              <div key={i} className="text-sm">
                <p className="text-zinc-500"><strong className="text-zinc-700">Appistry:</strong> {t.question}</p>
                {t.answer && <p className="text-zinc-800 mt-1"><strong>You:</strong> {t.answer}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Consents */}
      <Section title="Consents" icon={<ShieldCheck size={16} weight="fill" className="text-emerald-500" />}>
        <p className="text-sm text-zinc-700">
          Confidentiality: <strong>{sub.consent_confidentiality ? "Accepted" : "Not accepted"}</strong>
          <br />
          Privacy: <strong>{sub.consent_privacy ? "Accepted" : "Not accepted"}</strong>
        </p>
      </Section>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="bg-white border border-zinc-200 p-6 md:p-8 mb-6">
      <h2 className="text-sm font-mono uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">{icon} {title}</h2>
      {children}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-2">{label}</span>
      <span className="text-zinc-800">{value || "—"}</span>
    </div>
  );
}

function Block({ label, body }) {
  if (!body) return null;
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <p className="text-zinc-800 leading-relaxed whitespace-pre-line">{body}</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{value || "—"}</div>
    </div>
  );
}

function formatDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
